/** Null mode preserves legacy targeting; explicit empty selections match nothing. */
export type RuleScope = {
  properties?: string | null;
  propertyGroupIds?: string | null;
  scopeMode?: string | null;
  excludedPropertyIds?: string | null;
};

export function scopeIds(value: string | null | undefined): number[] {
  return (value ?? "").split(",").map(Number).filter(id => Number.isSafeInteger(id) && id > 0);
}

export function effectiveScopeMode(rule: RuleScope): "all" | "individual" | "group" {
  if (rule.scopeMode === "all" || rule.scopeMode === "individual" || rule.scopeMode === "group") return rule.scopeMode;
  return rule.propertyGroupIds?.trim() ? "group" : rule.properties?.trim() ? "individual" : "all";
}

/** Removals are sticky; only the editor's explicit exclusion list can undo them. */
export function preserveRuleExclusions(previous: RuleScope, next: RuleScope): string | null {
  const excluded = new Set(scopeIds(next.excludedPropertyIds === undefined ? previous.excludedPropertyIds : next.excludedPropertyIds));
  if (effectiveScopeMode(previous) === "individual" && effectiveScopeMode(next) === "individual") {
    const selected = new Set(scopeIds(next.properties));
    for (const id of scopeIds(previous.properties)) if (!selected.has(id)) excluded.add(id);
  }
  return [...excluded].sort((a, b) => a - b).join(",") || null;
}

/** Property onboarding may expand an explicit list, but never reverse a removal. */
export function copyRulePropertyScope(rule: RuleScope, sourceId: number, destinationId: number): string | null {
  const excluded = scopeIds(rule.excludedPropertyIds);
  if (excluded.includes(sourceId) || excluded.includes(destinationId)) return null;
  const ids = scopeIds(rule.properties);
  return ids.includes(sourceId) && !ids.includes(destinationId) ? [...ids, destinationId].join(",") : null;
}
